import { useNotify, useTranslate } from 'react-admin';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';

import { middle } from '../vaults/helpers';
import { RECORDS_NS as NS } from './constants';

export const NUMERIC = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const ID = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;

/** A long id shown cut in the middle, whole on hover, copied whole (as Investors shows an address). */
export const LongId = ({
  value,
  head,
  tail,
  caption,
}: {
  value: string;
  head?: number;
  tail?: number;
  caption?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { copy } = useCopyToClipboard();
  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', minWidth: 0 }}>
      <Tooltip title={value}>
        <Typography
          variant={caption ? 'caption' : 'body2'}
          sx={{ ...ID, fontSize: caption ? 12 : 13, color: caption ? 'text.secondary' : 'text.primary' }}
        >
          {middle(value, head, tail)}
        </Typography>
      </Tooltip>
      <IconButton
        size="small"
        aria-label={translate(`${NS}.copy`)}
        onClick={async () => {
          if (await copy(value)) notify(translate(`${NS}.copied`), { type: 'info' });
        }}
        sx={{ p: 0.25, color: 'text.disabled' }}
      >
        <Iconify icon="eva:copy-outline" width={14} />
      </IconButton>
    </Stack>
  );
};

/** The vault cell: its name, the id beneath. */
export const VaultCell = ({ name, vaultId }: { name: string; vaultId: string }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" noWrap>
      {name}
    </Typography>
    <Typography variant="caption" sx={{ ...ID, color: 'text.secondary' }}>
      {vaultId}
    </Typography>
  </Box>
);

/** A main line with a secondary line beneath it (a time and its instant; a figure and its note). */
export const Stacked = ({ main, note }: { main: React.ReactNode; note?: React.ReactNode }) => (
  <>
    <Box component="span" sx={NUMERIC}>
      {main}
    </Box>
    {note && (
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.25 }}>
        {note}
      </Typography>
    )}
  </>
);
